import React, { useEffect, useState } from 'react';
import { readinessApi } from '../../services/api';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';

const NUM_FIELDS = [
  { key: 'service_warning_days', label: 'Service due (days)' },
  { key: 'service_warning_hours', label: 'Service due (hours lead)' },
  { key: 'cert_warning_days', label: 'Certificate expiring (days)' },
  { key: 'periodic_inspection_days', label: 'Periodic inspection (days)' },
];

export const ReadinessSettings = () => {
  const [loading, setLoading] = useState(true);
  const [amber, setAmber] = useState(false);
  const [rows, setRows] = useState([]);
  const [savingCat, setSavingCat] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await readinessApi.getSettings();
      setAmber(!!data.amber_dispatchable);
      setRows(data.categories || []);
    } catch { toast.error('Could not load readiness settings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleAmber = async (v) => {
    setAmber(v);
    try { await readinessApi.putSettings({ amber_dispatchable: v }); toast.success(v ? 'Amber assets now count as dispatchable' : 'Only Green assets count as Ready'); }
    catch { setAmber(!v); toast.error('Could not update'); }
  };

  const setCell = (cat, key, val) => setRows(rows.map(r => r.category === cat ? { ...r, [key]: val } : r));

  const saveCategory = async (row) => {
    setSavingCat(row.category);
    try {
      const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
      await readinessApi.putConfig(row.category, {
        service_warning_days: num(row.service_warning_days),
        service_warning_hours: num(row.service_warning_hours),
        cert_warning_days: num(row.cert_warning_days),
        periodic_inspection_days: num(row.periodic_inspection_days),
      });
      toast.success(`${row.label} thresholds saved`);
    } catch { toast.error('Could not save thresholds'); }
    finally { setSavingCat(null); }
  };

  const recompute = async () => {
    setRecomputing(true);
    try { const { data } = await readinessApi.recompute(); toast.success(`Recomputed ${data.assets} assets`); }
    catch { toast.error('Recompute failed'); }
    finally { setRecomputing(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="readiness-settings">
      <div className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-semibold text-foreground">Amber assets are dispatchable</div>
          <p className="text-sm text-muted-foreground max-w-xl">When on, assets needing attention (Amber) still count as "Ready" in the yard. When off, only Green assets are Ready.</p>
        </div>
        <Switch checked={amber} onCheckedChange={toggleAmber} data-testid="amber-dispatchable-toggle" />
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div>
            <div className="font-semibold text-foreground">Thresholds by asset category</div>
            <p className="text-sm text-muted-foreground">Leave periodic inspection blank to ignore it for that category.</p>
          </div>
          <Button variant="outline" size="sm" onClick={recompute} disabled={recomputing} data-testid="readiness-recompute-btn">
            {recomputing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}Recompute now
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-secondary/40 border-b border-border">
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="text-left px-4 py-2">Category</th>
                {NUM_FIELDS.map(f => <th key={f.key} className="text-left px-3 py-2">{f.label}</th>)}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.category} className="border-b border-border" data-testid={`readiness-cat-${row.category}`}>
                  <td className="px-4 py-2 font-medium">{row.label}</td>
                  {NUM_FIELDS.map(f => (
                    <td key={f.key} className="px-3 py-2">
                      <Input type="number" value={row[f.key] ?? ''} onChange={(e) => setCell(row.category, f.key, e.target.value)} className="h-8 w-24" data-testid={`readiness-${row.category}-${f.key}`} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => saveCategory(row)} disabled={savingCat === row.category} data-testid={`readiness-save-${row.category}`}>
                      {savingCat === row.category ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReadinessSettings;
